import { inject, Injectable } from '@angular/core';
import { QuoteToolStrategyService } from '@lx/quote-tool/shared/services';
import { QuoteToolPreselectedModes } from '@lx/shared/utils';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { RouteIds } from '../../../types';
import { OnlineQuoteToolStore } from '../../../store';

/**
 * Default Mode Strategy for Online Quote Tool
 *
 * This strategy handles the default navigation behavior when no deep link is present.
 * It determines navigation based on the persisted state in session storage:
 *
 * - If currentStepId exists in session storage → Navigate to that step
 * - If no session storage → Navigate to the first step (build-your-course)
 *
 * @example
 * // User has previous session, navigates to last step
 * getStepToNavigate() // Returns currentStepId from store
 *
 * @example
 * // Fresh session, navigates to first step
 * getStepToNavigate() // Returns RouteIds.buildYourCourse
 */
@Injectable({
  providedIn: 'root',
})
export class DefaultModeStrategyService implements QuoteToolStrategyService<RouteIds> {
  private readonly store = inject(OnlineQuoteToolStore);

  /**
   * Returns the quote tool mode identifier
   */
  getQuoteToolMode(): QuoteToolPreselectedModes {
    return QuoteToolPreselectedModes.default;
  }

  /**
   * Returns the step to navigate to based on persisted state
   *
   * Priority:
   * 1. currentStepId from store (if exists in session storage)
   * 2. First step (build-your-course) as fallback
   *
   * @returns Observable of RouteIds indicating which step to navigate to
   */
  getStepToNavigate(): Observable<RouteIds> {
    return this.store.currentStepId$.pipe(
      map((currentStepId) => {
        // If we have a persisted step, navigate to it
        if (currentStepId) {
          return currentStepId;
        }

        // Otherwise, navigate to the first step
        return RouteIds.buildYourCourse;
      }),
    );
  }

  /**
   * Loads data for default mode
   *
   * For default mode, no additional data loading is required.
   * The store is already initialized with session storage data in APP_INITIALIZER.
   *
   * @returns Observable that emits true immediately (no async loading needed)
   */
  loadData$(): Observable<boolean> {
    // Default mode has nothing to load - state already loaded from session storage
    return new Observable((observer) => {
      observer.next(true);
      observer.complete();
    });
  }
}
